import {
  CONFIG_ELEMENTS,
  TestAnswerFeedbackDisplayOption,
  TestAnswerFeedbackSelectOption,
} from '@vedalib/editor/lib/elements'
import { rtValueToText, textToRtValue } from '@vedalib/rich-text'
import { useUpdateEffect } from 'ahooks'
import lodash from 'lodash'

import { buildTestAnnouncement, useLiveFeedback } from 'components/Editor/EditorElements/courseKit/CourseKitUtils'
import TestFeedback from 'components/Editor/EditorElements/courseKit/TestFeedback'
import { useElementRootStyles } from 'components/Editor/EditorElements/hooks/useElementRootStyles'
import { useShuffleRadioOrCheckboxItems } from 'components/Editor/EditorElements/hooks/useShuffleRadioOrCheckboxItems'
import { getFeedbackValue } from 'components/Editor/EditorElements/utils/getFeedbackValue'
import { RichTextViewer } from 'components/form/RichText/RichTextViewer'
import { setQuestion } from 'services/Store/Project/actions'
import { PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getBlockState } from 'services/Store/Project/selectors'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'
import { extendTestData } from 'utils/test/qaData'

import { CheckboxListItemStyled } from '../styled/CheckBoxListItemStyled'
import type { TestMultipleChoiceElementType } from '../testMultipleChoiceTypes'
import Checkbox from './Checkbox'

const TestMultipleChoiceElementPreview: TestMultipleChoiceElementType = ({
  element,
  mode,
  styles,
  block,
  state,
  font,
  feedback,
}) => {
  const isPdf = mode.previewMode === PreviewMode.pdf
  const { id: elementId, value } = element
  const dispatch = useProjectDispatch()
  const blockResult = useProjectContext(getBlockState, block?.uuid)?.result
  const feedbackValue = getFeedbackValue(value)

  const valueItems = value.items || []
  const disabled = Boolean(blockResult)
  const isRandom = value.isRandom && !isPdf
  const validIds = valueItems.filter((item) => item.isValid).map((item) => item.value)
  const randomIndexes = useShuffleRadioOrCheckboxItems(valueItems.length, isRandom)
  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: block?.uuid || '',
      elementType: CONFIG_ELEMENTS.testMultipleChoice.type,
      elementId,
    },
  )

  const isGeneralFb =
    value.displayFeedback && value.displayOption !== TestAnswerFeedbackDisplayOption.perOption
  const isOptionFb =
    value.displayFeedback && value.displayOption !== TestAnswerFeedbackDisplayOption.general

  const onInternalCheckboxChange = (checkedValue: string[]) => {
    const value = checkedValue.map((e) => e.toString())
    dispatch(
      setQuestion({
        elementId,
        value: { value },
        isReady: Boolean(checkedValue.length),
        blockId: block?.uuid || '',
      }),
    )
  }

  const rootStyles = useElementRootStyles(styles.root)
  const announce = useLiveFeedback('polite')

  useUpdateEffect(() => {
    if (!blockResult) {
      return
    }
    const displayedItems = isRandom ? randomIndexes.map((i) => valueItems[i]) : valueItems
    const selectedIds = state?.value || []

    const correctLabels = displayedItems
      .filter((item) => validIds.includes(item.value))
      .map((item) => rtValueToText(item.label))
    const yourLabels = displayedItems
      .filter((item) => selectedIds.includes(item.value))
      .map((item) => rtValueToText(item.label))

    announce(
      buildTestAnnouncement(
        blockResult.isValid,
        correctLabels.length ? correctLabels.join(', ') : undefined,
        yourLabels.length ? yourLabels.join(', ') : undefined,
      ),
    )
  }, [blockResult])

  return (
    <div aria-label='Multiple choice' role='group'>
      <ul style={rootStyles}>
        {valueItems.map((item, index) => {
          const randomIndex = randomIndexes[index]
          const itemToShow = isRandom ? valueItems[randomIndex] : item

          const currentIds = state?.value || []
          const isIncludeValidId = validIds && validIds.includes(itemToShow?.value)
          const isIncludeCurrentId = validIds && currentIds?.includes(itemToShow?.value)
          const isVisibleAnswer = isIncludeCurrentId || isIncludeValidId
          const selectedValues = state?.value || []

          const onChange = (value: boolean) => {
            const data = entityAnalyticsConfig.interacted({
              resultExtensions: {
                selectionItem: value ? itemToShow?.value : undefined,
                unSelectionItem: !value ? itemToShow?.value : undefined,
                labelSelectionItems: value ? rtValueToText(itemToShow?.label) : undefined,
                labelUnSelectionItems: !value ? rtValueToText(itemToShow?.label) : undefined,
              },
              objectExtensions: baseObjectExtensions,
              elementId: element.id,
            })
            sendXapiData(data)
            if (value) {
              onInternalCheckboxChange(lodash.uniq([...currentIds, itemToShow?.value]))
            } else {
              onInternalCheckboxChange(currentIds.filter((id: string) => id !== itemToShow?.value))
            }
          }

          const showFb = isPdf
            ? value.displayFeedback &&
              value.displayOption !== TestAnswerFeedbackDisplayOption.general
            : value.feedback === TestAnswerFeedbackSelectOption.selected && isOptionFb
              ? blockResult && selectedValues.includes(itemToShow.value)
              : isOptionFb

          if (itemToShow) {
            return (
              <CheckboxListItemStyled
                key={itemToShow.value}
                style={{ marginBottom: styles.offset.marginTop }}
              >
                <TestFeedback
                  blockUuid={block?.uuid}
                  feedbackValue={feedbackValue}
                  font={font}
                  isTrueAnswer={isIncludeValidId}
                  label={rtValueToText(itemToShow?.label)}
                  mode={mode}
                  rtProps={{
                    value: itemToShow?.feedback,
                  }}
                  showAnswerFb={!!isVisibleAnswer}
                  showFeedback={showFb}
                  styles={feedback}
                >
                  <Checkbox
                    checked={currentIds.includes(itemToShow.value)}
                    disabled={disabled}
                    onChange={onChange}
                    styles={styles}
                    testData={extendTestData({
                      label: itemToShow?.label ? rtValueToText(itemToShow?.label) : '',
                      index,
                    })}
                    value={item.value}
                  >
                    <RichTextViewer styles={font} value={itemToShow?.label || textToRtValue('')} />
                  </Checkbox>
                </TestFeedback>
              </CheckboxListItemStyled>
            )
          }
          return null
        })}
      </ul>
      <TestFeedback
        blockUuid={block?.uuid}
        feedbackValue={feedbackValue}
        font={font}
        isTrueAnswer={blockResult?.isValid}
        mode={mode}
        rtProps={{
          value: value.generalFeedback,
        }}
        showFeedback={isPdf ? isGeneralFb : isGeneralFb && !!blockResult}
        styles={feedback}
      />
    </div>
  )
}

export default TestMultipleChoiceElementPreview
